import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';

// Mock data - in real app, this would come from API
const mockProduct = {
  id: 1,
  name: 'Fresh Tomatoes',
  price: 150,
  unit: 'per kg',
  farmer: 'John Mwangi',
  location: 'Nakuru',
  image: '🍅',
  rating: 4.8,
  reviews: 24,
  description: 'Fresh, ripe tomatoes from our organic farm in Nakuru. Grown using sustainable farming practices and harvested at peak ripeness for maximum flavor and nutrition.',
  category: 'Vegetables',
  availability: 'In Stock',
  minOrder: 1,
  maxOrder: 50,
  deliveryTime: '1-2 days',
  farmerBio: 'John Mwangi has been farming for over 15 years in Nakuru. He specializes in organic vegetables and is committed to sustainable farming practices.',
  images: ['🍅', '🌱', '🚜', '👨‍🌾']
};

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  const handleAddToCart = () => {
    // In real app, this would add to cart
    alert(`Added ${quantity} ${mockProduct.unit} of ${mockProduct.name} to cart!`);
  };

  const handleBuyNow = () => {
    // In real app, this would proceed to checkout
    alert(`Proceeding to checkout with ${quantity} ${mockProduct.unit} of ${mockProduct.name}`);
  };

  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm text-gray-500">
            <li><Link to="/" className="hover:text-green-600">Home</Link></li>
            <li>/</li>
            <li><Link to="/products" className="hover:text-green-600">Products</Link></li>
            <li>/</li>
            <li className="text-gray-900">{mockProduct.name}</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div>
            <div className="aspect-square bg-gray-100 rounded-xl flex items-center justify-center text-8xl mb-4">
              {mockProduct.images[selectedImage]}
            </div>
            <div className="grid grid-cols-4 gap-2">
              {mockProduct.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`aspect-square bg-gray-100 rounded-lg flex items-center justify-center text-2xl ${
                    selectedImage === index ? 'ring-2 ring-green-500' : ''
                  }`}
                >
                  {image}
                </button>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div>
            <div className="mb-4">
              <span className="inline-block bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full mb-2">
                {mockProduct.category}
              </span>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{mockProduct.name}</h1>
              <div className="flex items-center mb-4">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className={`h-5 w-5 ${
                        i < Math.floor(mockProduct.rating) ? 'text-yellow-400' : 'text-gray-300'
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                  <span className="ml-2 text-sm text-gray-600">
                    {mockProduct.rating} ({mockProduct.reviews} reviews)
                  </span>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-baseline mb-4">
                <span className="text-4xl font-bold text-gray-900">KSh {mockProduct.price}</span>
                <span className="text-lg text-gray-500 ml-2">/{mockProduct.unit}</span>
              </div>
              <p className="text-gray-600 text-lg leading-relaxed">{mockProduct.description}</p>
            </div>

            {/* Farmer Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-900 mb-2">👨‍🌾 Farmer: {mockProduct.farmer}</h3>
              <p className="text-sm text-gray-600 mb-2">📍 Location: {mockProduct.location}</p>
              <p className="text-sm text-gray-600">{mockProduct.farmerBio}</p>
            </div>

            {/* Order Info */}
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Availability:</span>
                <span className="text-sm font-medium text-green-600">{mockProduct.availability}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Delivery Time:</span>
                <span className="text-sm font-medium text-gray-900">{mockProduct.deliveryTime}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Min Order:</span>
                <span className="text-sm font-medium text-gray-900">{mockProduct.minOrder} {mockProduct.unit}</span>
              </div>
            </div>

            {/* Quantity and Actions */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                  >
                    -
                  </button>
                  <span className="w-16 text-center font-medium">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(mockProduct.maxOrder, quantity + 1))}
                    className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                  >
                    +
                  </button>
                  <span className="text-sm text-gray-500">{mockProduct.unit}</span>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={handleAddToCart}
                  className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  Add to Cart
                </button>
                <button
                  onClick={handleBuyNow}
                  className="flex-1 bg-gray-900 text-white py-3 px-6 rounded-lg hover:bg-gray-800 transition-colors font-medium"
                >
                  Buy Now
                </button>
              </div>

              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Total: <span className="font-semibold">KSh {mockProduct.price * quantity}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Products</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* This would be populated with related products from API */}
            <div className="bg-white rounded-xl shadow-md p-4 text-center">
              <div className="text-4xl mb-2">🥬</div>
              <h3 className="font-semibold text-gray-900">Fresh Kale</h3>
              <p className="text-green-600 font-bold">KSh 50/bunch</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-4 text-center">
              <div className="text-4xl mb-2">🧅</div>
              <h3 className="font-semibold text-gray-900">Onions</h3>
              <p className="text-green-600 font-bold">KSh 200/kg</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-4 text-center">
              <div className="text-4xl mb-2">🥒</div>
              <h3 className="font-semibold text-gray-900">Cucumbers</h3>
              <p className="text-green-600 font-bold">KSh 80/piece</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-4 text-center">
              <div className="text-4xl mb-2">🌶️</div>
              <h3 className="font-semibold text-gray-900">Green Peppers</h3>
              <p className="text-green-600 font-bold">KSh 120/kg</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
